import React from "react";
import _ from "lodash";
import { NetworkAPI } from "apis";
import LoadingUI from "components/common/LoadingUI";
import PageTitle from "components/common/PageTitle";
import PromptModal from "components/common/PromptModal";
import ModalWrapper from "components/common/ModalWrapper";
import { buttonConfirmClass } from "helpers/StyleClass";
import NetworkInfoHeader from "../../common/NetworkInfoHeader";
import VirtualPlacementsTable from "./VirtualPlacementsTable";
import CreateOrUpdateVirtualPlacement from "./CreateOrUpdateVirtualPlacement";
import { MESSAGE } from "constants/Message";

class VirtualPlacementsViewer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      errMsg: null,

      isCreateModalOpen: false,
      isEditModalOpen: false,
      isPromptModalOpen: false,
      isSaving: false,
      promptMsg: [],
      promptHeader: "",
      promptMessage: "",
      promptAction: null,

      items: null,
      currentItem: null, // for edit
    };

    this.handleGoToEdit = this.handleGoToEdit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleUpdateData = this.handleUpdateData.bind(this);
  }

  async componentDidMount() {
    this.setState({ isLoading: true });
    try {
      const { networkId } = this.props.match.params;
      if (!networkId) {
        throw new Error("Missing Network ID");
      }
      const networkInfo = await NetworkAPI.getNetworkInfo({ networkId });
      if (!networkInfo) {
        throw new Error("Invalid Network");
      }

      await this.queryVirtualPlacements(networkId);

      this.setState({
        networkId,
        networkInfo,

        isLoading: false,
        errMsg: null,
      });
    } catch (err) {
      console.log(err);
      this.setState({
        isLoading: false,
        errMsg: err.toString(),
      });
    }
  }

  async queryVirtualPlacements(networkId) {
    const items = await NetworkAPI.getNetworkVirtualPlacements({
      networkId,
    });

    this.setState({
      items: _.orderBy(items, ["virtualPlacementId"], ["asc"]),
    });
  }

  handleGoToEdit(item) {
    this.setState({
      isEditModalOpen: true,
      currentItem: item,
    });
  }

  async handleUpdateData() {
    const { networkId } = this.state;
    if (networkId) {
      await this.queryVirtualPlacements(networkId);
    }
  }

  async handleDelete(item, isPrompt = true) {
    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptMsg: [],
        promptHeader: "Delete Virtual Placement",
        promptMessage: (
          <>
            Are you sure you want to delete virtual placement?
            <div className="font-semibold text-red-500">
              {item.virtualPlacementId} - {item.name}
            </div>
          </>
        ),
        promptAction: () => this.handleDelete(item, false),
      });
      return;
    }

    this.setState({
      isSaving: true,
    });

    try {
      const { networkId } = this.state;
      const { virtualPlacementId } = item;
      const params = {
        virtualPlacementId,
        networkId,
      };

      const r = await NetworkAPI.deleteNetworkVirtualPlacement(params);
      console.log(r);

      this.setState({
        promptMsg: ["Virtual Placement deleted!", MESSAGE.SUCCESS],
      });

      await this.queryVirtualPlacements(networkId);
    } catch (err) {
      console.log(err);
      this.setState({
        promptMsg: [`Failed to delete! ${err}`, MESSAGE.ERROR],
      });
    }

    this.setState({
      isSaving: false,
    });
  }

  render() {
    const {
      networkId,
      networkInfo,

      items,

      isLoading,
      errMsg,

      isCreateModalOpen,
      isEditModalOpen,
      isPromptModalOpen,
      isSaving,
      promptMsg,
      promptHeader,
      promptMessage,
      promptAction,

      currentItem,
    } = this.state;

    const modalTitle = () => {
      if (isCreateModalOpen) {
        return "Create Network Virtual Placement";
      } else if (isEditModalOpen) {
        return {
          label: "Update Network Virtual Placement ",
          highlight: currentItem
            ? `${currentItem.virtualPlacementId} - ${currentItem.name}`
            : "",
        };
      }
      return "";
    };

    return (
      <div>
        {isLoading && <LoadingUI></LoadingUI>}
        {errMsg && <div className="text-red-800">{errMsg}</div>}

        {networkInfo && (
          <div>
            <NetworkInfoHeader networkInfo={networkInfo}></NetworkInfoHeader>
            <div className="min-h-screen bg-white px-12 py-8">
              <div className="mb-2 flex items-center justify-between">
                <PageTitle title="Network Virtual Placements"></PageTitle>
              </div>

              <div className="mb-2 flex items-end justify-between gap-4">
                <div>{items ? items.length : 0} virtual placements</div>

                <button
                  type="button"
                  className={buttonConfirmClass}
                  onClick={() =>
                    this.setState({
                      isCreateModalOpen: true,
                    })
                  }
                >
                  Create new virtual placement
                </button>
              </div>

              <div>
                {items.length > 0 && (
                  <VirtualPlacementsTable
                    items={items}
                    networkId={networkId}
                    timezone={networkInfo.timezone}
                    handleEditClicked={this.handleGoToEdit}
                    handleDelete={this.handleDelete}
                  ></VirtualPlacementsTable>
                )}
              </div>
            </div>
            <ModalWrapper
              isOpen={isCreateModalOpen || isEditModalOpen}
              showCloseFooter={false}
              customOverlayStyle={{
                zIndex: 100,
              }}
              width="50%"
              title={modalTitle()}
            >
              {isCreateModalOpen && (
                <CreateOrUpdateVirtualPlacement
                  formType="ADD"
                  virtualPlacement={{}}
                  networkId={networkId}
                  handleClose={() =>
                    this.setState({ isCreateModalOpen: false })
                  }
                  handleUpdate={this.handleUpdateData}
                ></CreateOrUpdateVirtualPlacement>
              )}
              {isEditModalOpen && (
                <CreateOrUpdateVirtualPlacement
                  formType="UPDATE"
                  virtualPlacement={currentItem}
                  networkId={networkId}
                  handleClose={() => this.setState({ isEditModalOpen: false })}
                  handleUpdate={this.handleUpdateData}
                ></CreateOrUpdateVirtualPlacement>
              )}
            </ModalWrapper>
          </div>
        )}

        {isPromptModalOpen && (
          <PromptModal
            msg={promptMsg}
            isLoading={isSaving}
            isOpenModal={isPromptModalOpen}
            handleConfirm={promptAction}
            handleCancel={() => this.setState({ isPromptModalOpen: false })}
            header={promptHeader}
          >
            {promptMessage}
          </PromptModal>
        )}
      </div>
    );
  }
}

export default VirtualPlacementsViewer;
